import { useContext, useCallback } from 'react';
import { Icon, Tooltip, Typography } from '@scale/scaleui';
import { RadioCard } from '../../../RadioCard';
import { Draggable } from 'react-beautiful-dnd';
import { CandidateMessageWithMetadata } from '@scale/shared/types/chat';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';

type Props = {
  candidate: CandidateMessageWithMetadata;
  draggableIndex: number;
  candidateDisplayIndex: number;
  shouldObfuscate?: boolean;
  obfuscationLength?: number;
  isDragDisabled?: boolean;
  enableRTLText?: boolean;
  onExpandClick?: () => void;
};

const DraggableCandidateCard = ({
  candidate,
  candidateDisplayIndex,
  draggableIndex,
  shouldObfuscate,
  obfuscationLength,
  isDragDisabled = false,
  enableRTLText,
  onExpandClick,
}: Props) => {
  const taskContext = useContext(ChatTaskContext);

  const obfuscate = useCallback(
    (message: string) => {
      return shouldObfuscate ? message.slice(0, obfuscationLength) : message;
    },
    [obfuscationLength, shouldObfuscate],
  );

  return (
    <Draggable
      key={`draggablecomponent-${candidate.id}`}
      draggableId={`dragId-${candidate.id}`}
      index={draggableIndex}
      isDragDisabled={isDragDisabled}
    >
      {provided => (
        <div
          key={candidate.id}
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
          }}
          {...provided.dragHandleProps}
          className="mb-2"
        >
          <RadioCard
            value={candidate.id}
            key={`${candidate.id}-${candidate.index}-${candidate.state}`}
            className="h-full min-[800px]:flex-1 max-[800px]:w-96"
            customHeader={
              <div className="flex items-center justify-between w-full px-4 py-3 cursor-grab">
                <div className="flex items-center">
                  <Icon variant="neutral" size="sm" icon="grip-vertical" />
                  <div>
                    <Typography.Subtitle4 color="text-neutral-500">
                      Response {candidateDisplayIndex + 1}
                    </Typography.Subtitle4>
                  </div>
                </div>
                {onExpandClick && (
                  <Tooltip
                    text="Expand response"
                    side="top"
                    target={
                      <div>
                        <Icon variant="neutral" size="sm" icon="expand" onClick={onExpandClick} />
                      </div>
                    }
                  />
                )}
              </div>
            }
            disabled={true}
          >
            <div className="overflow-auto h-[100px] cursor-grab">
              {candidate.state === 'completed' && (
                <ChatMarkdown
                  className="text-sm"
                  enableRTL={enableRTLText}
                  markdown={obfuscate(candidate.message.content)}
                  enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                  enableSingleDollarTextMath={
                    taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
                  }
                  enableHtml={taskContext.htmlMarkdownConfig?.enableResponsePreviews}
                  enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
                  enableExecutableCodeBlocks={
                    taskContext.codingConfig?.enableExecutableCodeMarkdown ??
                    taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
                  }
                />
              )}
              {candidate.state === 'error' && (
                <div className="flex flex-col items-center gap-2 p-3">
                  <div className="flex items-center text-danger-400">
                    <Icon variant="danger" size="sm" icon="exclamation-circle" />
                    Could not connect to the model.
                  </div>
                </div>
              )}
            </div>
          </RadioCard>
        </div>
      )}
    </Draggable>
  );
};

export default DraggableCandidateCard;
